/**
 * Performance Collector
 * Collects navigation timing, resource timing, and memory usage
 */

import { BaseCollector } from './BaseCollector.js';
import type { EventBus } from '../core/EventBus.js';
import type { PerformanceEvent, ResourceType } from '../models/PerformanceEvent.js';

export class PerformanceCollector extends BaseCollector {
  private navigationTimingCollected: boolean = false;
  private loadListener: (() => void) | null = null;

  constructor(
    eventBus: EventBus,
    sessionId: string,
    userId: string | null,
    debug: boolean = false
  ) {
    super('PerformanceCollector', eventBus, sessionId, userId, debug);
  }

  initialize(): void {
    this.log('Initializing performance tracking');

    // Collect navigation timing after page load
    if (document.readyState === 'complete') {
      this.collectNavigationTiming();
    } else {
      this.loadListener = () => {
        setTimeout(() => this.collectNavigationTiming(), 0);
      };
      window.addEventListener('load', this.loadListener);
    }

    // Collect memory usage periodically (Chrome only)
    this.collectMemoryUsage();

    this.isInitialized = true;
  }

  private collectNavigationTiming(): void {
    if (this.navigationTimingCollected) return;

    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    if (navigation && navigation.loadEventEnd > 0) {
      const baseMetadata = this.getBaseMetadata();
      
      const loadComplete = navigation.loadEventEnd - navigation.fetchStart;
      const domContentLoaded = navigation.domContentLoadedEventEnd > 0
        ? navigation.domContentLoadedEventEnd - navigation.fetchStart
        : null;

      const performanceEvent: PerformanceEvent = {
        ...baseMetadata,
        event_category: 'performance',
        event_type: 'page_load',
        load_time: Math.round(loadComplete),
        dom_content_loaded: domContentLoaded ? Math.round(domContentLoaded) : null,
        redirect_time: Math.round(navigation.redirectEnd - navigation.redirectStart),
        dns_lookup_time: Math.round(navigation.domainLookupEnd - navigation.domainLookupStart),
        tcp_connect_time: Math.round(navigation.connectEnd - navigation.connectStart),
        server_response_time: Math.round(navigation.responseEnd - navigation.requestStart),
        resource_url: null,
        resource_type: null,
        resource_duration: null,
        resource_size: null,
        resource_encoded_size: null,
        resource_decoded_size: null,
        resource_cached: null,
        resource_protocol: null,
        memory_used_js_heap: null,
        memory_total_js_heap: null,
        memory_js_heap_limit: null,
        navigation_type: 'page-load',
        previous_url: null
      };

      this.emit('event:collected', performanceEvent);
      this.log(`Collected page load metrics: ${loadComplete}ms`);

      this.navigationTimingCollected = true;

      // Collect resource timing after navigation timing
      this.collectResourceTiming();
    }
  }

  private collectResourceTiming(): void {
    const collectResources = () => {
      const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
      
      resources.forEach(resource => {
        const baseMetadata = this.getBaseMetadata();
        
        // Determine resource type
        let resourceType: ResourceType = 'other';
        const url = resource.name;
        
        if (resource.initiatorType === 'img') {
          resourceType = 'image';
        } else if (resource.initiatorType === 'script') {
          resourceType = 'script';
        } else if (resource.initiatorType === 'link' || resource.initiatorType === 'css') {
          resourceType = 'stylesheet';
        } else if (url.match(/\.(woff|woff2|ttf|otf|eot)$/i)) {
          resourceType = 'font';
        } else if (resource.initiatorType === 'xmlhttprequest' || resource.initiatorType === 'fetch') {
          // Skip XHR/fetch as they're tracked separately by NetworkCollector
          return;
        } else if (url.match(/\.(html|htm)$/i)) {
          resourceType = 'document';
        } else if (url.match(/\.(json|xml)$/i)) {
          resourceType = 'data';
        } else if (url.match(/\.(mp4|webm|ogg|mp3|wav)$/i)) {
          resourceType = 'media';
        }

        const cached = resource.transferSize === 0 && resource.decodedBodySize > 0;

        const performanceEvent: PerformanceEvent = {
          ...baseMetadata,
          event_category: 'performance',
          event_type: 'resource_load',
          load_time: null,
          dom_content_loaded: null,
          redirect_time: null,
          dns_lookup_time: null,
          tcp_connect_time: null,
          server_response_time: null,
          resource_url: resource.name,
          resource_type: resourceType,
          resource_duration: Math.round(resource.duration),
          resource_size: resource.transferSize,
          resource_encoded_size: resource.encodedBodySize,
          resource_decoded_size: resource.decodedBodySize,
          resource_cached: cached,
          resource_protocol: resource.nextHopProtocol || null,
          memory_used_js_heap: null,
          memory_total_js_heap: null,
          memory_js_heap_limit: null,
          navigation_type: null,
          previous_url: null
        };

        this.emit('event:collected', performanceEvent);
      });

      this.log(`Collected ${resources.length} resource timings`);
    };

    // Defer to idle time or use timeout fallback
    if ('requestIdleCallback' in window) {
      requestIdleCallback(collectResources, { timeout: 2000 });
    } else {
      setTimeout(collectResources, 100);
    }
  }

  private collectMemoryUsage(): void {
    try {
      if ('memory' in performance) {
        const memory = (performance as any).memory;
        if (memory) {
          const baseMetadata = this.getBaseMetadata();

          const performanceEvent: PerformanceEvent = {
            ...baseMetadata,
            event_category: 'performance',
            event_type: 'memory_usage',
            load_time: null,
            dom_content_loaded: null,
            redirect_time: null,
            dns_lookup_time: null,
            tcp_connect_time: null,
            server_response_time: null,
            resource_url: null,
            resource_type: null,
            resource_duration: null,
            resource_size: null,
            resource_encoded_size: null,
            resource_decoded_size: null,
            resource_cached: null,
            resource_protocol: null,
            memory_used_js_heap: memory.usedJSHeapSize,
            memory_total_js_heap: memory.totalJSHeapSize,
            memory_js_heap_limit: memory.jsHeapSizeLimit,
            navigation_type: null,
            previous_url: null
          };

          this.emit('event:collected', performanceEvent);
          this.log('Collected memory usage');
        }
      }
    } catch (error) {
      this.log('Failed to collect memory usage (not supported)');
    }
  }

  destroy(): void {
    if (this.loadListener) {
      window.removeEventListener('load', this.loadListener);
      this.loadListener = null;
    }

    this.isInitialized = false;
    this.log('Destroyed');
  }
}

